// frontend/src/content/professions/registry.js
//
// THE list of profession landing pages. Adding a profession is a content
// module (`./<slug>.js`), ONE line here, and the page's sitemap `<loc>` - there
// is no route-table line to write. Everything else derives from this file:
// src/appRoutes.js generates one <Route> per entry (through the generic
// pages/professions/ProfessionPage.js), the prerender's ROUTES and its
// per-route section contract, HomeNav's nav preset, the SEO sync tests'
// assertions, and the content-module suite
// (__tests__/professionContentSchema.test.js fails until the new module is
// added to its MODULES list, so no page skips the content rules).
//
// TWO HARD CONSTRAINTS, and they are why this file looks the way it does:
//
//   1. IT IS COMMONJS. `scripts/prerender.js` is a plain `node` script with no
//      babel in front of it, and it has to read this list (the blog slugs
//      above it are hand-mirrored for exactly that reason, with a test to catch
//      the drift - this file removes the need for that trade). ESM consumers
//      import it normally: webpack and babel-jest both hand a `module.exports`
//      object to a default import, which is why every consumer here takes the
//      DEFAULT and destructures rather than using named imports (named imports
//      off a CJS module rely on static-analysis interop that neither tool
//      guarantees for this shape).
//
//   2. IT HOLDS NO CONTENT AND NO REACT. It is imported by the app's eager
//      graph (appRoutes.js and HomeNav), so a profession's copy must never be reachable
//      from here: a content module pulled in here would ride in the main
//      bundle for every visitor, and a typo in one profession's strings would
//      blank the whole SPA instead of one route. Slugs and routes only.

/**
 * The anchor ids EVERY profession page carries, in render order.
 *
 * One list, three consumers: the page (`ProfessionLanding.js` re-exports it),
 * the prerender's per-route contract (each id must be present and carry text
 * in the snapshot, or the deploy hard-fails) and the invariant tests. It used
 * to be two hand-kept copies in two files kept in step by a test; it is one
 * now, so there is nothing left to drift.
 *
 * THE TICKER, THE MID-PAGE CTA PANEL, THE CLOSING CTA AND THE HERO'S OWN BAND
 * ARE DELIBERATELY ABSENT, on the homepage's precedent: none of them carries an
 * `id`, and getElementById on an id nothing renders is a hard fail. They are
 * atmosphere and asks, not anchors.
 */
const PROFESSION_SECTION_IDS = [
  "hero",
  "clients",
  "where",
  "how-it-works",
  // The homepage's own benefits grid and opportunity-types trio, mounted
  // verbatim (owner decision 2026-09-22). `features` lands in its exact
  // homepage slot, right after how-it-works; the profession's own concrete
  // `opportunity` cards lead, and the generic `opportunity-types` widen them -
  // specific before generic.
  "features",
  "opportunity",
  "opportunity-types",
  "pricing",
  "numbers",
  "faq",
  // The blog strip, not a bespoke cross-links block: the page ends on the same
  // three-card editorial slot the homepage does, with this profession's
  // articles pinned into it (BlogLive's SectionShell id).
  "blog",
];

/**
 * One entry per live profession page.
 *
 * `slug` is the content module's filename and the route's last segment; they
 * are the same string by construction so a page cannot be registered at a URL
 * its content does not claim (the content module states `route` too, and
 * `seoContentSync.test.js` asserts the two agree).
 */
const PROFESSION_PAGES = [
  { slug: "lawyers", route: "/lawyers" },
  { slug: "accountants", route: "/accountants" },
  { slug: "insurance-agents", route: "/insurance-agents" },
  { slug: "digital-marketers", route: "/digital-marketers" },
  { slug: "dentists", route: "/dentists" },
  { slug: "dog-trainers", route: "/dog-trainers" },
  { slug: "web-developers", route: "/web-developers" },
];

/** Every profession page's route, in registration order. */
function professionRoutes() {
  return PROFESSION_PAGES.map((page) => page.route);
}

/** The registry entry for a slug, or null. */
function professionBySlug(slug) {
  return PROFESSION_PAGES.find((page) => page.slug === slug) || null;
}

/**
 * `{ route: sectionIds }` for every profession page - the shape
 * `scripts/prerender.js` folds straight into its SECTION_CONTRACTS map.
 *
 * Every page renders the same sections (that is what ProfessionLanding IS), so
 * they all resolve to the one list above. A fresh array per call, so a caller
 * that sorts or splices its copy cannot reach back into the registry.
 */
function professionSectionContracts() {
  const contracts = {};
  PROFESSION_PAGES.forEach((page) => {
    contracts[page.route] = PROFESSION_SECTION_IDS.slice();
  });
  return contracts;
}

module.exports = {
  PROFESSION_SECTION_IDS,
  PROFESSION_PAGES,
  professionRoutes,
  professionBySlug,
  professionSectionContracts,
};
